import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { api_url_v1 } from '../utils/config';


let token = null;

try {
  token = window.localStorage.getItem('adminToken');
} catch {
  token = null;
}

// Get all customers
export const getCustomers = createAsyncThunk(
  'customers/getCustomers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${api_url_v1}/getCustomers`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        params: {
          _cb: new Date().getTime()
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customers');
    }
  }
);

// Block customer
export const blockCustomer = createAsyncThunk(
  'customers/blockCustomer',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${api_url_v1}/blockCustomer`,
        {
          customerId: id
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );
      return { id, data: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to block customer');
    }
  }
);

// Unblock customer
export const unblockCustomer = createAsyncThunk(
  'customers/unblockCustomer',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${api_url_v1}/unblockCustomer`,
        {
          customerId: id
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );
      return { id, data: response.data.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to unblock customer');
    }
  }
);

// Initial state
const initialState = {
  customers: [],
  filteredCustomers: [],
  selectedCustomer: null,
  loading: false,
  error: null,
  success: false,
  message: ''
};

// Create slice
const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.message = '';
    },
    setSelectedCustomer: (state, action) => {
      state.selectedCustomer = action.payload;
    },
    filterCustomers: (state, action) => {
      const { searchTerm, status } = action.payload;
      
      state.filteredCustomers = state.customers?.filter(customer => {
        const customerName = `${customer.firstName || ''} ${customer.lastName || ''}`.toLowerCase();
        const customerEmail = customer.email?.toLowerCase() || '';
        
        const matchesSearch = searchTerm
          ? customerName.includes(searchTerm.toLowerCase()) ||
            customerEmail.includes(searchTerm.toLowerCase())
          : true;
        
        const matchesStatus = status && status !== 'all'
          ? (status === 'blocked' ? customer.isBlocked : !customer.isBlocked)
          : true;
        
        return matchesSearch && matchesStatus;
      });
    },
    resetFilteredCustomers: (state) => {
      state.filteredCustomers = state.customers;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get all customers
      .addCase(getCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload;
        state.filteredCustomers = action.payload;
        state.error = null;
      })
      .addCase(getCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Block customer
      .addCase(blockCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(blockCustomer.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update customer in both arrays
        const updateCustomer = (customer) => {
          if (customer.id === action.payload.id) {
            return { ...customer, isBlocked: true };
          }
          return customer;
        };
        
        state.customers = state.customers.map(updateCustomer);
        state.filteredCustomers = state.filteredCustomers.map(updateCustomer);
        
        state.success = true;
        state.message = 'Customer blocked successfully';
        state.error = null;
      })
      .addCase(blockCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      
      // Unblock customer
      .addCase(unblockCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(unblockCustomer.fulfilled, (state, action) => {
        state.loading = false;
        
        // Update customer in both arrays
        const updateCustomer = (customer) => {
          if (customer.id === action.payload.id) {
            return { ...customer, isBlocked: false };
          }
          return customer;
        };
        
        state.customers = state.customers.map(updateCustomer);
        state.filteredCustomers = state.filteredCustomers.map(updateCustomer);
        
        state.success = true;
        state.message = 'Customer unblocked successfully';
        state.error = null;
      })
      .addCase(unblockCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  }
});

export const {
  clearError,
  clearSuccess,
  setSelectedCustomer,
  filterCustomers,
  resetFilteredCustomers
} = customerSlice.actions;

export default customerSlice.reducer;